import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import FetchWithSpinner from 'components/fetch-with-spinner'
import QuestionResponseDialogue from 'interview/components/question-response-dialogue'
import { mapLanguageToComponentProps } from 'interview/i18n'
import {
  fetchCandidate,
  completeVideo,
  saveDraft,
  removeDraft,
} from 'interview/actions/candidate'
import { USE_FILE_UPLOAD } from 'features'
import find from 'lodash/fp/find'

import * as analytics from 'analytics/performance'
import { PageContent } from 'components/styled/page/page-content'
import {
  getCandidateInfoForAmplitude,
  getCompanyAndInterviewInfoForAmplitude,
} from 'selectors'

const propTypes = {
  candidate: PropTypes.shape({
    _id: PropTypes.string,
    uuid: PropTypes.string,
    videoResponses: PropTypes.array,
  }).isRequired,
  jobOpening: PropTypes.shape({
    questions: PropTypes.array,
    company: {
      _id: PropTypes.string,
      companyName: PropTypes.string,
    },
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  params: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({
    query: PropTypes.shape({}),
  }).isRequired,
  globalRecorder: PropTypes.string,
}

const defaultProps = {
  globalRecorder: '',
}

const mapStateToProps = state => ({
  candidate: state.candidate,
  jobOpening: state.interview.jobOpening,
  globalRecorder: state.global.recorder,
})

export class EmbeddedInterview extends Component {
  constructor() {
    super()

    this.state = {
      clientHeight: 0,
      interviewCompanyData: {},
      candidateInfo: {},
    }

    this.onVideoChange = this.onVideoChange.bind(this)
    this.onVideoSubmit = this.onVideoSubmit.bind(this)
    this.onVideoRemove = this.onVideoRemove.bind(this)
    this.onHeightChange = this.onHeightChange.bind(this)
  }

  componentDidMount() {
    const { candidate } = this.props
    if (candidate && candidate.uuid) {
      this.setCompanyAndCandidateData()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { candidate } = this.props
    const { clientHeight } = this.state

    if (prevProps.candidate !== candidate && candidate && candidate.uuid) {
      this.setCompanyAndCandidateData()
    }
    if (prevState.clientHeight !== clientHeight) {
      window.parent.postMessage(['rrHeight', clientHeight], '*')
    } else if (clientHeight === 0) {
      this.onHeightChange()
    }
  }

  /**
   * When video is changed dispatch changes to backend
   */
  onVideoChange(questionId) {
    const { candidate, dispatch, globalRecorder } = this.props
    const { interviewCompanyData, candidateInfo } = this.state
    const videoResponse = find({ questionId })(candidate.videoResponses)

    dispatch(
      saveDraft({
        isMobile: USE_FILE_UPLOAD || globalRecorder === 'upload',
        uuid: candidate.uuid,
        response: videoResponse,
      })
    )
    analytics.onChangeEvent({
      locationId: 'embeddedInterviewPage',
      id: 'video',
      properties: {
        isEmbedded: true,
        ...candidateInfo,
        ...interviewCompanyData,
      },
    })
  }

  /**
   * When user submits single video
   */
  onVideoSubmit(questionId) {
    const { candidate, dispatch } = this.props
    const { interviewCompanyData } = this.state
    const videoResponse = find({ questionId })(candidate.videoResponses)

    dispatch(
      completeVideo({
        uuid: candidate.uuid,
        response: { ...videoResponse, embedded: true },
      })
    )
    analytics.onClickEvent({
      locationId: 'embeddedInterviewPage',
      id: 'videoSubmitButton',
      properties: {
        isEmbedded: true,
        candidateId: candidate._id,
        ...interviewCompanyData,
      },
    })
  }

  /**
   * When user removes draft
   */
  onVideoRemove(questionId) {
    const { candidate, dispatch } = this.props
    const { interviewCompanyData } = this.state
    const videoResponse = find({ questionId })(candidate.videoResponses)

    dispatch(
      removeDraft({
        uuid: candidate.uuid,
        response: videoResponse,
      })
    )
    analytics.onChangeEvent({
      locationId: 'embeddedInterviewPage',
      id: 'videoDraftRemoveButton',
      properties: {
        isEmbedded: true,
        candidateId: candidate._id,
        ...interviewCompanyData,
      },
    })
  }

  onHeightChange() {
    const container = document.getElementById('embedded-content')
    const contentHeight = container ? container.clientHeight : false
    if (contentHeight) {
      // Add 100 pixels to give space to help button and error messages
      const clientHeight = contentHeight + 100
      if (clientHeight !== this.state.clientHeight) {
        this.setState({ clientHeight })
      }
    }
  }

  setCompanyAndCandidateData() {
    const { jobOpening, candidate } = this.props
    const interviewCompanyData =
      getCompanyAndInterviewInfoForAmplitude(jobOpening)
    const candidateInfo = getCandidateInfoForAmplitude(candidate)

    this.setState({ interviewCompanyData, candidateInfo })
  }

  render() {
    const { candidate, jobOpening, params } = this.props
    const { interviewCompanyData, candidateInfo } = this.state

    return (
      <>
        <PageContent size="small" mt="s24" mb="s24">
          <div id="embedded-content">
            <FetchWithSpinner action={() => fetchCandidate(params)}>
              <QuestionResponseDialogue
                questions={jobOpening.questions}
                responses={candidate.videoResponses}
                useFileUpload={USE_FILE_UPLOAD}
                uuid={candidate.uuid}
                onHeightChange={this.onHeightChange}
                onVideoSubmit={this.onVideoSubmit}
                onVideoChange={this.onVideoChange}
                onVideoRemove={this.onVideoRemove}
                locationId="embeddedInterviewPage"
                videoEventProps={{
                  ...interviewCompanyData,
                  ...candidateInfo,
                  isEmbedded: true,
                }}
              />
            </FetchWithSpinner>
          </div>
        </PageContent>
      </>
    )
  }
}

EmbeddedInterview.propTypes = propTypes
EmbeddedInterview.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  EmbeddedInterview
)
